import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set to a user object right after a successful customer login/signup so
  // a celebration modal can render once and then clear itself.
  const [celebrateUser, setCelebrateUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("jumlaty_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then((res) => setUser(res.data))
        .catch((err) => {
          // Only wipe the token when the server EXPLICITLY says the token
          // is invalid (401/403). A network hiccup, CORS glitch, cold
          // WebView bootup, or a transient 5xx must NOT log the user out —
          // that was the "app installs but shows only the login page"
          // symptom reported during Google Play closed testing.
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem("jumlaty_token");
            delete axios.defaults.headers.common["Authorization"];
          } else {
            // Retry once after a short delay before giving up — WebViews
            // on Android sometimes race the network on cold start.
            setTimeout(() => {
              axios.get(`${API}/auth/me`)
                .then((res) => setUser(res.data))
                .catch((e2) => {
                  const s2 = e2?.response?.status;
                  if (s2 === 401 || s2 === 403) {
                    localStorage.removeItem("jumlaty_token");
                    delete axios.defaults.headers.common["Authorization"];
                  }
                  // On repeated network failure keep the token; user can
                  // retry manually. Better to show empty content than to
                  // silently log them out.
                });
            }, 1500);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (phone, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { phone, password });
    localStorage.setItem("jumlaty_token", data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setUser(data.user);
    // Celebration only for customers — staff/admin don't need it on every
    // login. We set this AFTER setUser so AppShell can read user on render.
    if (data.user?.role === "user") setCelebrateUser(data.user);
    return data.user;
  };

  const signup = async (data) => {
    const { data: res } = await axios.post(`${API}/auth/signup`, data);
    localStorage.setItem("jumlaty_token", res.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.token}`;
    setUser(res.user);
    if (res.user?.role === "user") setCelebrateUser(res.user);
    return res.user;
  };

  const driverSignup = async (data) => {
    const { data: res } = await axios.post(`${API}/auth/driver-signup`, data);
    localStorage.setItem("jumlaty_token", res.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.token}`;
    setUser(res.user);
    return res.user;
  };

  const mujahezSignup = async (data) => {
    const { data: res } = await axios.post(`${API}/auth/mujahez-signup`, data);
    localStorage.setItem("jumlaty_token", res.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.token}`;
    setUser(res.user);
    return res.user;
  };

  const supervisorSignup = async (data) => {
    const { data: res } = await axios.post(`${API}/auth/supervisor-signup`, data);
    localStorage.setItem("jumlaty_token", res.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.token}`;
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    localStorage.removeItem("jumlaty_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  // Re-fetches the current authenticated user from /auth/me. Used after
  // self-service profile edits in `MyAccount` so the header / sidebar pick
  // up the new name / phone / market without forcing a re-login.
  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, signup, driverSignup, mujahezSignup, supervisorSignup, logout,
      refreshUser,
      celebrateUser, dismissCelebration: () => setCelebrateUser(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
